/**
 * Люди на полных кадрах — правка боксов для дообучения детектора людей (пул «Люди»).
 *
 * Тот же инструмент, что «Боксы» (BoxAnnotator), но всегда на ВЕСЬ кадр камеры и без полок.
 * Боксы приходят заполненными: наш детектор (зелёные), внешняя предразметка там, где нашего
 * бокса нет (жёлтые), дорисованные человеком — фиолетовые. Мышь и клавиши — общие
 * (`boxDraw.tsx`); клавиши по коду, чтобы работали и в русской раскладке.
 *
 * Правки копятся синими, одна кнопка «Сохранить» отправляет пачку; «Разметка корректная (N)»
 * подтверждает нетронутые кадры страницы.
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import {
  CheckCheck, ChevronLeft, ChevronRight, Loader2, RefreshCw, Save, Undo2, ZoomIn, ZoomOut,
} from 'lucide-react';
import { useT } from '@/contexts/I18nContext';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Tooltip } from '@/components/ui/tooltip';
import { BOX_VIEWS, type BoxView } from '@/api/boxPool';
import {
  getPeoplePoolSamples, getPeoplePoolZones, peoplePoolAnnotate,
  type PersonBox, type PersonItem, type PersonLabelDelta,
} from '@/api/peoplePool';
import {
  BoxCanvas, FULL_VIEW, boxStyle, fmtTime, sameBoxes, type BoxStroke, type Dims, type Rect,
} from './boxDraw';

const PAGE_SIZE = 24;
const MIN_COLS = 1;
const MAX_COLS = 4;

/** Цвет по источнику; выбранный / удалённый / дорисованный / слабый — как в «Боксах». */
const personStyle = (b: PersonBox, selected: boolean): BoxStroke => {
  if (selected || b.deleted || b.added || b.weak) return boxStyle(b, selected);
  if (b.source === 'external') return { stroke: '#f59e0b', strokeWidth: 2, fill: 'rgba(245,158,11,0.10)', dash: undefined };
  if (b.source === 'fixed') return { stroke: '#06b6d4', strokeWidth: 2, fill: 'rgba(6,182,212,0.10)', dash: undefined };
  return boxStyle(b, selected);
};

const newPersonBox = (r: Rect): PersonBox => ({
  id: null, ...r, score: 1, weak: false, deleted: false, added: true, source: 'human',
});

// ── card ────────────────────────────────────────────────────────────────────────

interface CardProps {
  it: PersonItem;
  boxes: PersonBox[];
  selected: Set<number>;
  dims: Dims | undefined;
  focused: boolean;
  pending: 'modified' | 'correct' | null;
  showWeak: boolean;
  onFocus: () => void;
  onSelect: (sel: Set<number>) => void;
  onChange: (boxes: PersonBox[]) => void;
  onUnlabel?: () => void;
}

const PersonCard = memo(function PersonCard({
  it, boxes, selected, dims, focused, pending, showWeak, onFocus, onSelect, onChange, onUnlabel,
}: CardProps) {
  const { t } = useT();
  const aspect = dims ? `${dims.w} / ${dims.h}` : '16 / 9';
  const visible = (b: PersonBox) => showWeak || !b.weak;
  const live = boxes.filter(b => !b.deleted && !b.weak);
  const ours = live.filter(b => !b.added && (b.source === 'ours' || !b.source)).length;
  const fixed = live.filter(b => !b.added && b.source === 'fixed').length;
  const external = live.filter(b => !b.added && b.source === 'external').length;
  const added = live.filter(b => b.added).length;
  const deleted = boxes.filter(b => b.deleted).length;
  const tone = pending === 'modified' ? 'bg-blue-100 ring-2 ring-blue-300'
    : pending === 'correct' ? 'bg-emerald-50 ring-2 ring-emerald-300'
      : 'bg-white ring-1 ring-border';

  return (
    <div
      onClick={onFocus}
      data-key={it.key}
      className={`relative rounded-lg p-1.5 ${tone} ${focused ? 'z-10 outline outline-4 outline-blue-600 outline-offset-2' : ''}`}
    >
      <div className="mb-1 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <Tooltip content={`frame ${it.frame_id}`}><span className="truncate">
          📍 {it.zone ?? it.zone_id} · {it.camera ?? it.camera_id} · {fmtTime(it.ts)}
        </span></Tooltip>
        {it.label && (
          <Tooltip content={it.label.annotated_by ?? ''}><span className="shrink-0 rounded bg-muted px-1 text-[10px]">
            {t(`data.boxes.status.${it.label.status}`)}
          </span></Tooltip>
        )}
      </div>

      <BoxCanvas
        imageUrl={it.image_url}
        view={FULL_VIEW}
        aspect={aspect}
        boxes={boxes}
        selected={selected}
        visible={visible}
        styleOf={personStyle}
        newBox={newPersonBox}
        onFocus={onFocus}
        onSelect={onSelect}
        onChange={onChange}
      />

      <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px]">
        <span className="tabular-nums text-muted-foreground">
          {fixed ? <span className="text-cyan-700">{fixed} {t('data.people.fixed')} · </span> : null}
          <span className="text-emerald-700">{ours} {t('data.people.ours')}</span>
          {external ? <span className="text-amber-700"> · {external} {t('data.people.external')}</span> : null}
          {added ? <span className="text-purple-700"> · +{added}</span> : null}
          {deleted ? <span className="text-rose-700"> · −{deleted}</span> : null}
        </span>
        {onUnlabel && (
          <button type="button" className="ml-auto rounded px-1.5 py-0.5 text-muted-foreground hover:bg-muted"
                  onClick={(e) => { e.stopPropagation(); onUnlabel(); }}>
            {t('data.boxes.unlabel')}
          </button>
        )}
      </div>
    </div>
  );
});

// ── annotator ───────────────────────────────────────────────────────────────────

export default function PeopleAnnotator() {
  const { t } = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCollection = Number(searchParams.get('collection')) || null;
  const [collection, setCollection] = useState<number | null>(urlCollection);
  const [zoneId, setZoneId] = useState<number | null>(null);
  const [view, setView] = useState<BoxView>('queue');
  const [page, setPage] = useState(1);
  const [cols, setCols] = useState(2);
  const [focus, setFocus] = useState(0);
  const [showWeak, setShowWeak] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState('');
  // Правки этой сессии: key → боксы. Нет ключа — карточка нетронута.
  const [edits, setEdits] = useState<Record<string, PersonBox[]>>({});
  const history = useRef<Record<string, PersonBox[][]>>({});
  const [marks, setMarks] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Record<string, Set<number>>>({});
  const [dims, setDims] = useState<Record<string, Dims>>({});

  const pickCollection = (c: number | null) => {
    setCollection(c);
    setPage(1);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (c) next.set('collection', String(c)); else next.delete('collection');
      return next;
    }, { replace: true });
  };

  const { data: zonesResp, mutate: reloadZones } = useSWR('people-pool-zones',
    () => getPeoplePoolZones().then(r => r.data));
  const zones = zonesResp?.zones ?? [];
  const collections = zonesResp?.collections ?? [];

  const { data, isLoading, mutate } = useSWR(
    ['people-pool', collection ?? 0, zoneId ?? 0, view, page] as const,
    () => getPeoplePoolSamples({ zone_id: zoneId, collection, view, page, page_size: PAGE_SIZE }).then(r => r.data),
    { revalidateOnFocus: false, keepPreviousData: true },
  );
  const items = useMemo(() => data?.items ?? [], [data]);
  useEffect(() => { setFocus(0); }, [collection, zoneId, view, page]);
  useEffect(() => { setFocus(f => Math.min(f, Math.max(0, items.length - 1))); }, [items.length]);

  useEffect(() => {
    for (const it of items) {
      if (!it.image_url || dims[it.key]) continue;
      const im = new Image();
      im.onload = () => setDims(prev => (prev[it.key] ? prev : { ...prev, [it.key]: { w: im.naturalWidth, h: im.naturalHeight } }));
      im.src = it.image_url;
    }
  }, [items, dims]);

  const boxesOf = useCallback((it: PersonItem) => edits[it.key] ?? it.boxes, [edits]);
  const isModified = useCallback((it: PersonItem) => it.key in edits && !sameBoxes(edits[it.key], it.boxes), [edits]);
  const pendingOf = useCallback((it: PersonItem): 'modified' | 'correct' | null =>
    (isModified(it) ? 'modified' : marks.has(it.key) ? 'correct' : null), [isModified, marks]);

  const change = useCallback((key: string, before: PersonBox[], next: PersonBox[]) => {
    (history.current[key] ??= []).push(before);
    setEdits(prev => ({ ...prev, [key]: next }));
  }, []);
  const undo = useCallback((it: PersonItem) => {
    const h = history.current[it.key];
    if (!h?.length) return;
    const prev = h.pop()!;
    setEdits(e => ({ ...e, [it.key]: prev }));
    setSelected(s => ({ ...s, [it.key]: new Set() }));
  }, []);

  const deleteSelected = useCallback((it: PersonItem) => {
    const sel = selected[it.key];
    if (!sel?.size) return;
    const cur = boxesOf(it);
    // Дорисованный бокс просто исчезает; пришедший помечается — его надо видеть, чтобы вернуть.
    const next = cur.map((b, i) => (sel.has(i) && !b.added ? { ...b, deleted: true } : b))
      .filter((b, i) => !(sel.has(i) && b.added));
    change(it.key, cur, next);
    setSelected(s => ({ ...s, [it.key]: new Set() }));
  }, [selected, boxesOf, change]);

  const restoreSelected = useCallback((it: PersonItem) => {
    const sel = selected[it.key];
    if (!sel?.size) return;
    const cur = boxesOf(it);
    if (!cur.some((b, i) => sel.has(i) && b.deleted)) return;
    change(it.key, cur, cur.map((b, i) => (sel.has(i) ? { ...b, deleted: false } : b)));
  }, [selected, boxesOf, change]);

  const toggleMark = useCallback((it: PersonItem) => {
    if (isModified(it)) return;
    setMarks(prev => { const n = new Set(prev); if (n.has(it.key)) n.delete(it.key); else n.add(it.key); return n; });
  }, [isModified]);

  const pendingItems = useMemo(() => items.filter(it => pendingOf(it)), [items, pendingOf]);
  const untouched = useMemo(() => items.filter(it => !pendingOf(it) && it.label?.status !== 'correct'),
    [items, pendingOf]);

  const send = useCallback(async (deltas: PersonLabelDelta[]) => {
    if (!deltas.length) return;
    setBusy(true);
    try {
      const r = await peoplePoolAnnotate(deltas);
      const keys = new Set(items.filter(it => deltas.some(d => d.label_id === it.label_id)).map(it => it.key));
      setEdits(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => !keys.has(k))));
      setMarks(prev => new Set([...prev].filter(k => !keys.has(k))));
      for (const k of keys) delete history.current[k];
      setSaved(`✓ ${t('data.boxes.saved')} ${r.data.saved}`);
      window.setTimeout(() => setSaved(s => (s.startsWith('✓') ? '' : s)), 2500);
      await mutate();
      void reloadZones();
    } catch {
      setSaved(t('data.boxes.saveFailed'));
      window.setTimeout(() => setSaved(''), 4000);
    } finally {
      setBusy(false);
    }
  }, [items, mutate, reloadZones, t]);

  const saveAll = useCallback(() => send(pendingItems.map(it => ({
    label_id: it.label_id, status: pendingOf(it)!, boxes: boxesOf(it),
  }))), [send, pendingItems, pendingOf, boxesOf]);

  /** Подтвердить страницу: нетронутые — «корректно», плюс всё, что уже ждёт сохранения. */
  const confirmPage = useCallback(() => send([
    ...pendingItems.map(it => ({ label_id: it.label_id, status: pendingOf(it)!, boxes: boxesOf(it) })),
    ...untouched.map(it => ({ label_id: it.label_id, status: 'correct' as const, boxes: it.boxes })),
  ]), [send, pendingItems, untouched, pendingOf, boxesOf]);

  const unlabel = useCallback((it: PersonItem) => {
    void send([{ label_id: it.label_id, status: null }]);
  }, [send]);

  const move = useCallback((dir: 'left' | 'right' | 'up' | 'down') => {
    setFocus(f => {
      const n = items.length;
      if (!n) return 0;
      if (dir === 'right') return Math.min(n - 1, f + 1);
      if (dir === 'left') return Math.max(0, f - 1);
      if (dir === 'down') return Math.min(n - 1, f + cols);
      return Math.max(0, f - cols);
    });
  }, [items.length, cols]);

  useEffect(() => {
    const it = items[focus];
    if (!it) return;
    document.querySelector(`[data-key="${it.key}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [focus, items]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT' || tag === 'SELECT') return;
      const it = items[focus];
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.code === 'KeyS') { e.preventDefault(); void saveAll(); return; }
      if (e.code === 'ArrowRight') { e.preventDefault(); move('right'); return; }
      if (e.code === 'ArrowLeft') { e.preventDefault(); move('left'); return; }
      if (e.code === 'ArrowDown') { e.preventDefault(); move('down'); return; }
      if (e.code === 'ArrowUp') { e.preventDefault(); move('up'); return; }
      if (e.code === 'KeyW' && !mod) { setShowWeak(v => !v); return; }
      if (!it) return;
      if (e.code === 'KeyD' || e.code === 'Delete' || e.code === 'Backspace') { e.preventDefault(); deleteSelected(it); return; }
      if (e.code === 'KeyR' && !mod) { restoreSelected(it); return; }
      if (e.code === 'KeyZ') { e.preventDefault(); undo(it); return; }
      if (e.code === 'Escape') { setSelected(s => ({ ...s, [it.key]: new Set() })); return; }
      if (e.code === 'Space' || e.code === 'Enter') { e.preventDefault(); toggleMark(it); move('right'); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items, focus, move, saveAll, deleteSelected, restoreSelected, undo, toggleMark]);

  // Несохранённое не должно пропасть от случайного F5.
  useEffect(() => {
    if (!pendingItems.length) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [pendingItems.length]);

  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const facets = data?.facets;
  const queueTotal = zonesResp?.totals.queue ?? 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-card px-3 py-2">
        <Select
          className="h-8" wrapperClassName="w-auto"
          value={collection ?? 'all'}
          onChange={e => { const v = e.target.value; pickCollection(v === 'all' ? null : Number(v)); }}
        >
          <option value="all">{t('data.people.allCollections')}</option>
          {collection && !collections.some(c => c.id === collection) && <option value={collection}>#{collection}</option>}
          {collections.map(c => (
            <option key={c.id} value={c.id}>{c.name} ({c.queue})</option>
          ))}
        </Select>

        <Select
          className="h-8" wrapperClassName="w-auto"
          value={zoneId ?? 'all'}
          onChange={e => { const v = e.target.value; setZoneId(v === 'all' ? null : Number(v)); setPage(1); }}
        >
          <option value="all">{t('data.boxes.allZones')} ({queueTotal})</option>
          {zones.map(z => (
            <option key={z.zone_id} value={z.zone_id}>{z.zone ?? z.zone_id} ({z.queue})</option>
          ))}
        </Select>

        <Tooltip content={t('data.people.queueHint')}><div className="inline-flex overflow-hidden rounded-md border text-xs">
          {BOX_VIEWS.map(v => (
            <button
              key={v} type="button"
              className={`px-2.5 py-1 ${view === v
                ? (v === 'queue' ? 'bg-amber-100 text-amber-900' : v === 'modified' ? 'bg-blue-100 text-blue-900' : 'bg-emerald-100 text-emerald-900')
                : 'text-muted-foreground'}`}
              onClick={() => { setView(v); setPage(1); }}
            >
              {t(`data.boxes.view.${v}`)} ({facets?.[v] ?? 0})
            </button>
          ))}
        </div></Tooltip>

        <div className="inline-flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-7 w-7" title={t('data.annotate.zoomIn')}
                  disabled={cols <= MIN_COLS} onClick={() => setCols(c => Math.max(MIN_COLS, c - 1))}>
            <ZoomIn className="h-4 w-4" />
          </Button>
          <span className="w-4 text-center text-xs text-muted-foreground">{cols}</span>
          <Button variant="outline" size="icon" className="h-7 w-7" title={t('data.annotate.zoomOut')}
                  disabled={cols >= MAX_COLS} onClick={() => setCols(c => Math.min(MAX_COLS, c + 1))}>
            <ZoomOut className="h-4 w-4" />
          </Button>
        </div>

        <Tooltip content={t('data.boxes.showWeakHint')}><label className="inline-flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={showWeak} onChange={e => setShowWeak(e.target.checked)} />
          {t('data.boxes.showWeak')}
        </label></Tooltip>

        <span className="text-[11px] text-muted-foreground">{t('data.people.keys')}</span>

        <div className="ml-auto flex items-center gap-2">
          <span className={`text-xs ${saved.startsWith('✓') ? 'text-emerald-600' : 'text-amber-700'}`}>{saved}</span>
          <span className="text-xs text-muted-foreground tabular-nums">
            {items.length ? Math.min(focus + 1, items.length) : 0} / {items.length} · {t('data.boxes.page')} {page}/{pages} · {total}
          </span>
          <Button variant="outline" size="sm" onClick={() => { void mutate(); void reloadZones(); }} title={t('data.pool.refreshHint')}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" disabled={!items[focus] || !history.current[items[focus].key]?.length}
                  onClick={() => items[focus] && undo(items[focus])} title="Z">
            <Undo2 className="h-4 w-4" />
          </Button>
          <Button size="sm" disabled={busy || !pendingItems.length} onClick={() => void saveAll()}
                  className={pendingItems.length ? 'bg-blue-600 hover:bg-blue-700' : ''} title="Ctrl/⌘+S">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
            {t('data.boxes.save')} ({pendingItems.length})
          </Button>
          {view === 'queue' && (
            <Button size="sm" variant="outline" disabled={busy || (!untouched.length && !pendingItems.length)}
                    onClick={() => void confirmPage()} title={t('data.boxes.confirmHint')}>
              <CheckCheck className="mr-1.5 h-4 w-4" />
              {t('data.boxes.confirm')} ({untouched.length})
            </Button>
          )}
        </div>
      </div>

      {isLoading && !data && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}</div>
      )}
      {data && !items.length && (
        <div className="py-10 text-center text-sm text-muted-foreground">{t('data.boxes.empty')}</div>
      )}

      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {items.map((it, i) => (
          <PersonCard
            key={it.key}
            it={it}
            boxes={boxesOf(it)}
            selected={selected[it.key] ?? new Set()}
            dims={dims[it.key]}
            focused={i === focus}
            pending={pendingOf(it)}
            showWeak={showWeak}
            onFocus={() => setFocus(i)}
            onSelect={(sel) => setSelected(s => ({ ...s, [it.key]: sel }))}
            onChange={(next) => change(it.key, boxesOf(it), next)}
            onUnlabel={view !== 'queue' ? () => unlabel(it) : undefined}
          />
        ))}
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 text-sm">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="tabular-nums">{page} / {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
